import { HttpLibrary } from './shared-http'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FetchResponse {
  data: any
  status: number
  headers: Record<string, string>
}

type ParamValue = string | number | boolean | (string | number | boolean)[]

export interface FetchRequestConfig {
  headers?: Record<string, string>
  params?: Record<string, ParamValue>
  responseType?: string
  /** Milliseconds; same semantics as Axios `timeout`. */
  timeout?: number
  signal?: AbortSignal
}

export interface FetchHttpError extends Error {
  statusCode?: number
  /** Present on axios-compatible timeout errors (`ECONNABORTED`). */
  code?: string
  response?: {
    status: number
    statusCode?: number
    data: any
    headers: Record<string, string>
    request?: { responseURL: string }
  }
  config?: any
  isAxiosError?: boolean
}

// ---------------------------------------------------------------------------
// Pure utilities
// ---------------------------------------------------------------------------

/**
 * Resolves the active HTTP library from a static env-flag value and
 * the global `window.__USE_FETCH_HTTP_CLIENT__` override.
 *
 * Callers must pass the statically-resolvable env reference
 * (e.g. `process.env.USE_FETCH_HTTP_CLIENT_ADMIN`) so that Vite's
 * `define` can substitute it at build time.
 */
export function getHttpLibraryFromEnv(
  envFlagValue: string | undefined
): HttpLibrary {
  if (
    envFlagValue === 'true' ||
    (typeof window !== 'undefined' &&
      (window as { __USE_FETCH_HTTP_CLIENT__?: boolean })
        .__USE_FETCH_HTTP_CLIENT__)
  ) {
    return HttpLibrary.Fetch
  }
  return HttpLibrary.Axios
}

/**
 * Joins a base URL with a path and appends query params.
 * Handles array values with `key[]=val` encoding (compatible with qs).
 */
export function buildUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, any>
): string {
  const url = path.startsWith('http')
    ? path
    : `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`
  if (!params || Object.keys(params).length === 0) return url
  const search = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue
    if (Array.isArray(v)) {
      for (const item of v) {
        if (item === undefined || item === null) continue
        search.append(`${k}[]`, String(item))
      }
    } else {
      search.set(k, String(v))
    }
  }
  const qs = search.toString()
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url
}

/** Converts a browser `Headers` object to a plain record. */
export function headersToRecord(headers: Headers): Record<string, string> {
  const out: Record<string, string> = {}
  headers.forEach((v, k) => {
    out[k] = v
  })
  return out
}

// ---------------------------------------------------------------------------
// Fetch request helpers
// ---------------------------------------------------------------------------

/**
 * Reads the response body according to `responseType`.
 * Falls back to JSON-aware text parsing when no explicit type is given.
 */
export async function parseFetchResponseBody(
  response: Response,
  responseType?: string
): Promise<any> {
  if (responseType === 'arraybuffer') return response.arrayBuffer()
  if (responseType === 'blob') return response.blob()
  const contentType = response.headers.get('content-type') ?? ''
  const text = await response.text()
  if (contentType.includes('application/json') && text) {
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }
  return text || null
}

/** Creates a standardised error object for a failed fetch response. */
export function createFetchHttpError(
  status: number,
  data: any,
  headers: Record<string, string>,
  url?: string
): FetchHttpError {
  const err = new Error(
    `Request failed with status ${status}`
  ) as FetchHttpError
  err.statusCode = status
  err.response = {
    status,
    statusCode: status,
    data,
    headers,
  }
  if (url) {
    err.response.request = { responseURL: url }
  }
  return err
}

/**
 * Builds a `RequestInit` from method, headers and body.
 * Handles FormData (strips Content-Type) and auto-JSON-serialises objects.
 */
export function prepareFetchInit(
  method: string,
  headers: Record<string, string>,
  body?: any,
  credentials: RequestCredentials = 'same-origin'
): RequestInit {
  const reqHeaders = { ...headers }
  const fetchInit: RequestInit = {
    method: method.toUpperCase(),
    headers: reqHeaders,
    credentials,
  }
  if (method !== 'get' && !reqHeaders['Content-Type']) {
    reqHeaders['Content-Type'] = 'application/json'
  }
  if (body != null && method !== 'get') {
    if (body instanceof FormData) {
      fetchInit.body = body
      delete reqHeaders['Content-Type']
    } else {
      fetchInit.body = typeof body === 'string' ? body : JSON.stringify(body)
    }
  }
  return fetchInit
}

// ---------------------------------------------------------------------------
// Abort / timeout (Axios-compatible)
// ---------------------------------------------------------------------------

export function combineAbortSignals(signals: AbortSignal[]): AbortSignal {
  const filtered = signals.filter(Boolean)
  if (filtered.length === 1) return filtered[0]
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any(filtered)
  }
  const combined = new AbortController()
  const handlers: Array<{ signal: AbortSignal; fn: () => void }> = []

  const forward = (_source: AbortSignal) => {
    if (!combined.signal.aborted) {
      combined.abort(_source.reason)
    }
    for (const { signal, fn } of handlers) {
      signal.removeEventListener('abort', fn)
    }
    handlers.length = 0
  }

  for (const s of filtered) {
    if (s.aborted) {
      forward(s)
      break
    }
    const fn = () => forward(s)
    handlers.push({ signal: s, fn })
    s.addEventListener('abort', fn)
  }
  return combined.signal
}

export interface FetchInitTimeoutOptions {
  timeout?: number
  signal?: AbortSignal
}

/**
 * Merges optional Axios-style `timeout` and `signal` into `RequestInit`.
 * Call `cancelTimeout()` in `finally` after the request completes.
 */
export function buildFetchInitWithTimeoutAndSignal(
  fetchInitBase: RequestInit,
  options: FetchInitTimeoutOptions
): {
  fetchInit: RequestInit
  cancelTimeout: () => void
  timeoutController: AbortController | undefined
  timeoutMs: number
} {
  const timeoutMs =
    typeof options.timeout === 'number' &&
    options.timeout > 0 &&
    Number.isFinite(options.timeout)
      ? options.timeout
      : 0
  const userSignal = options.signal

  let timeoutController: AbortController | undefined
  let cancelTimeout: () => void = () => {}

  if (timeoutMs === 0 && !userSignal) {
    return {
      fetchInit: fetchInitBase,
      cancelTimeout,
      timeoutController: undefined,
      timeoutMs: 0,
    }
  }

  const signals: AbortSignal[] = []
  if (userSignal) signals.push(userSignal)
  if (timeoutMs > 0) {
    timeoutController = new AbortController()
    const id = globalThis.setTimeout(
      () => timeoutController!.abort(),
      timeoutMs
    )
    cancelTimeout = () => globalThis.clearTimeout(id)
    signals.push(timeoutController.signal)
  }

  const fetchInit =
    signals.length > 0
      ? {
          ...fetchInitBase,
          signal: combineAbortSignals(signals),
        }
      : fetchInitBase

  return { fetchInit, cancelTimeout, timeoutController, timeoutMs }
}

/** Synthetic error matching axios timeout failures for shared error handling. */
export function createAxiosCompatibleTimeoutError(
  timeoutMs: number,
  config: unknown
): FetchHttpError {
  const err = new Error(`timeout of ${timeoutMs}ms exceeded`) as FetchHttpError
  err.name = 'AxiosError'
  err.code = 'ECONNABORTED'
  err.config = config
  err.isAxiosError = true
  return err
}

/**
 * True when `error` is an `AbortError` caused by our request timeout (not a
 * user-only `signal` abort).
 *
 * `buildFetchInitWithTimeoutAndSignal` only calls `timeoutController.abort()`
 * from the scheduled timer callback; a user abort goes through the merged
 * `AbortSignal` and does not flip `timeoutController.signal` until that timer
 * runs. So `timeoutController.signal.aborted` means “timer fired and aborted
 * the timer controller,” not a wall-clock race between unrelated tasks.
 */
export function isFetchTimeoutAbortError(
  error: unknown,
  timeoutController: AbortController | undefined,
  timeoutMs: number
): boolean {
  return (
    (error as { name?: string })?.name === 'AbortError' &&
    timeoutMs > 0 &&
    Boolean(timeoutController?.signal.aborted)
  )
}

// ---------------------------------------------------------------------------
// Retry utilities
// ---------------------------------------------------------------------------

export const IDEMPOTENT_HTTP_METHODS: ReadonlySet<string> = new Set([
  'get',
  'head',
  'options',
  'put',
  'delete',
])

/**
 * Whether a failed request may be retried. Timeouts (`ECONNABORTED`), user
 * aborts (`AbortError` / `ERR_CANCELED`), and cancellations are not retried
 * (previously they had no `response` and were incorrectly treated as retryable).
 */
export function isRetryableError(error: any, method: string): boolean {
  if (error?.code === 'ECONNABORTED') return false
  if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
    return false
  }
  if (!error.response) return true
  if (IDEMPOTENT_HTTP_METHODS.has(method) && error.response.status >= 500)
    return true
  return false
}

// ---------------------------------------------------------------------------
// FetchClient — axios-compatible shape backed by a pluggable executor
// ---------------------------------------------------------------------------

export type FetchRequestExecutor<
  C extends FetchRequestConfig = FetchRequestConfig,
> = (
  baseUrl: string,
  method: string,
  path: string,
  config?: C,
  body?: any
) => Promise<FetchResponse>

export class FetchClient<C extends FetchRequestConfig = FetchRequestConfig> {
  constructor(
    private baseUrl: string,
    private execute: FetchRequestExecutor<C>
  ) {}

  async get<T = any>(
    path: string,
    config?: C
  ): Promise<{ data: T } & FetchResponse> {
    return this.execute(this.baseUrl, 'get', path, config)
  }

  async post<T = any>(
    path: string,
    data?: any,
    config?: C
  ): Promise<{ data: T } & FetchResponse> {
    return this.execute(this.baseUrl, 'post', path, config, data)
  }

  async put<T = any>(
    path: string,
    data?: any,
    config?: C
  ): Promise<{ data: T } & FetchResponse> {
    return this.execute(this.baseUrl, 'put', path, config, data)
  }

  async patch<T = any>(
    path: string,
    data?: any,
    config?: C
  ): Promise<{ data: T } & FetchResponse> {
    return this.execute(this.baseUrl, 'patch', path, config, data)
  }

  async delete<T = any>(
    path: string,
    config?: C & { data?: any }
  ): Promise<{ data: T } & FetchResponse> {
    const { data, ...restConfig } = (config ?? {}) as C & { data?: any }
    return this.execute(this.baseUrl, 'delete', path, restConfig as C, data)
  }
}
